/**
 * Organization Switcher for Super Admins
 *
 * Allows super admins to select which organization to act as
 * when accessing portal features. Uses the unified OrgContext
 * for reactive state management across the portal.
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getAuthTokenAsync } from '@/services/auth/token-manager';
import { config } from '@/utils/env';
import { Button } from '@/components/ui/button';
import { Building2, ChevronDown, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useOrgContext } from '@/contexts/OrgContext';

interface Organization {
  id: string;
  name: string;
  slug: string;
  plan: string;
  active: boolean;
}

async function fetchOrganizations(): Promise<Organization[]> {
  const token = await getAuthTokenAsync();
  const response = await fetch(`${config.apiUrl}/admin/organizations`, {
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
  if (!response.ok) throw new Error('Failed to fetch organizations');
  return response.json();
}

interface OrgSwitcherProps {
  /** Compact mode for header display */
  compact?: boolean;
}

export function OrgSwitcher({ compact = false }: OrgSwitcherProps) {
  const { orgId, orgName, setOrg } = useOrgContext();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);

  const { data: organizations, isLoading } = useQuery({
    queryKey: ['admin', 'organizations'],
    queryFn: fetchOrganizations,
  });

  const activeOrgs = organizations?.filter(o => o.active) || [];

  if (compact) {
    return (
      <div className="relative">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setIsOpen(!isOpen)}
          className="gap-2"
        >
          <Building2 className="h-4 w-4" />
          <span className="max-w-[120px] truncate">
            {orgName || 'Select org'}
          </span>
          <ChevronDown className="h-3 w-3" />
        </Button>

        {isOpen && (
          <>
            <div
              className="fixed inset-0 z-40"
              onClick={() => setIsOpen(false)}
            />
            <div className="absolute right-0 top-full mt-1 z-50 w-56 rounded-md border bg-popover p-1 shadow-md">
              {isLoading ? (
                <div className="px-3 py-2 text-sm text-muted-foreground">Loading...</div>
              ) : (
                activeOrgs.map((org) => (
                  <button
                    key={org.id}
                    onClick={() => {
                      setOrg(org.id, org.name);
                      setIsOpen(false);
                    }}
                    className={`w-full text-left px-3 py-2 text-sm rounded-sm hover:bg-accent transition-colors ${
                      org.id === orgId ? 'bg-accent font-medium' : ''
                    }`}
                  >
                    <div>{org.name}</div>
                    <div className="text-xs text-muted-foreground">{org.slug} &middot; {org.plan}</div>
                  </button>
                ))
              )}
            </div>
          </>
        )}
      </div>
    );
  }

  // Full-page org picker (shown when no org selected)
  return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <div className="w-full max-w-md space-y-6">
        <div className="text-center space-y-2">
          <Building2 className="h-12 w-12 mx-auto text-muted-foreground" />
          <h2 className="text-xl font-semibold">Select Organization</h2>
          <p className="text-sm text-muted-foreground">
            Choose which organization to manage in the portal.
          </p>
        </div>

        <div className="space-y-2">
          {isLoading ? (
            <div className="text-center text-sm text-muted-foreground py-4">
              Loading organizations...
            </div>
          ) : activeOrgs.length === 0 ? (
            <div className="text-center text-sm text-muted-foreground py-4">
              No active organizations found.
            </div>
          ) : (
            activeOrgs.map((org) => (
              <button
                key={org.id}
                onClick={() => {
                  setOrg(org.id, org.name);
                  navigate('/portal/dashboard');
                }}
                className="w-full text-left px-4 py-3 rounded-lg border hover:border-primary hover:bg-accent transition-colors"
              >
                <div className="font-medium">{org.name}</div>
                <div className="text-xs text-muted-foreground">{org.slug} &middot; {org.plan}</div>
              </button>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

export function BackToAdminLink() {
  const navigate = useNavigate();
  const { clearOrg } = useOrgContext();

  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={() => {
        clearOrg();
        navigate('/dashboard');
      }}
      className="w-full justify-start text-muted-foreground hover:text-foreground"
    >
      <ArrowLeft className="h-4 w-4 mr-2" />
      Back to Admin
    </Button>
  );
}
